/* istanbul ignore file
TODO: Add test for expiration validation
*/
import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  createContext,
  useContext
} from 'react';
import { useFormContext } from 'react-hook-form';

import { PaymentInformationProps } from 'payments/components/clover/CloverPaymentInformation';
import { handleSaveCardOnFileMutation } from 'payments/helpers/handleSaveCardOnFileMutation';
import { type CardFieldStates } from 'payments/types/card';
import type {
  PartialSaveOptions,
  PaymentFormContextInterface,
  SaveCardOnFileMutation
} from 'payments/types/form';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { validateExpiration } from 'reservations/payments/helpers/validateExpiration';

const CloverPaymentFormContext =
  createContext<PaymentFormContextInterface | null>(null);

interface ProviderProps extends PaymentInformationProps {
  handleCardSubmit: () => Promise<any>;
  setCardFieldStates: Dispatch<SetStateAction<CardFieldStates>>;
}

export const CloverPaymentFormProvider = ({
  handleCardSubmit,
  setCardFieldStates,
  bookingDate,
  children
}: PropsWithChildren<ProviderProps>) => {
  const { handleHasLoaded } = useLoadingSpinner();
  const { setValue } = useFormContext();

  const processCard = async () => {
    let { card, token, errors } = await handleCardSubmit();

    if (card) {
      const expirationIsValid = validateExpiration(
        card.exp_month,
        card.exp_year,
        new Date(bookingDate)
      );

      if (!expirationIsValid) {
        const errorMessage =
          'Your card expires before the reservation date. Please choose a different card.';

        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          expirationDate: {
            ...currentFieldStates.CARD_DATE,
            error: errorMessage
          }
        }));
        errors = {
          CARD_DATE: errorMessage
        };
      }
    }
    handleHasLoaded();
    return { card, token, errors };
  };

  const handlePaymentForm = async (): Promise<boolean> => {
    const { card, token, errors } = await handleCardSubmit();

    if (errors || !token) {
      return false;
    }

    if (card) {
      const expirationIsValid = validateExpiration(
        card.exp_month,
        card.exp_year,
        new Date(bookingDate)
      );

      if (!expirationIsValid) {
        const errorMessage =
          'Your card expires before the reservation date. Please choose a different card.';

        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          expirationDate: {
            ...currentFieldStates.CARD_DATE,
            error: errorMessage
          }
        }));
        return false;
      }
    }

    setValue('token', token);
    setValue('cardDetails', card);
    return true;
  };

  const saveCardOnFile = async (
    saveOptions: PartialSaveOptions,
    mutate: SaveCardOnFileMutation
  ) => {
    const { card, token, errors } = await processCard();

    if (errors || !token || !card) {
      return;
    }

    setValue('token', token);
    setValue('cardDetails', card);

    handleSaveCardOnFileMutation(saveOptions, mutate, token, card);
  };

  const retrieveToken = async () => {
    const { card, token, errors } = await processCard();

    if (errors || !token || !card) {
      return;
    }

    setValue('token', token);
    setValue('cardDetails', card);
  };

  return (
    <CloverPaymentFormContext.Provider
      value={{ retrieveToken, saveCardOnFile, handlePaymentForm }}
    >
      {children}
    </CloverPaymentFormContext.Provider>
  );
};

export const useCloverPaymentForm = () => useContext(CloverPaymentFormContext);
